import { ReactNode, useState } from "react";
import { AuditStatus } from "@/lib/shopConfigAudit";
import { cn } from "@/lib/utils";
import { STATUS_STYLES } from "./statusStyles";

const Chip = ({
  status,
  children,
}: {
  status: AuditStatus;
  children: ReactNode;
}) => (
  <span
    className={cn(
      "rounded-full px-2 py-0.5 text-xs font-semibold",
      STATUS_STYLES[status].chip
    )}
  >
    {children}
  </span>
);

interface ShopConfigAuditSectionProps {
  title: string;
  missing: number;
  drift: number;
  error?: boolean;
  children: ReactNode;
}

// Collapsed state is local: the panel re-keys its section container per shop,
// so a new selection starts expanded (as in the mockup).
export const ShopConfigAuditSection = ({
  title,
  missing,
  drift,
  error,
  children,
}: ShopConfigAuditSectionProps) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-white text-foreground">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-background"
      >
        <span className="flex items-center gap-2">
          <span aria-hidden className="w-2.5 text-xs text-muted-foreground">
            {open ? "▾" : "▸"}
          </span>
          <span className="text-[15px] font-semibold">{title}</span>
        </span>
        <span className="flex flex-wrap justify-end gap-1.5">
          {error && <Chip status="missing">Error</Chip>}
          {!error && missing > 0 && (
            <Chip status="missing">{missing} not set</Chip>
          )}
          {!error && drift > 0 && <Chip status="drift">{drift} drifted</Chip>}
          {!error && missing === 0 && drift === 0 && (
            <Chip status="match">All match</Chip>
          )}
        </span>
      </button>
      {open && <div className="border-t border-border">{children}</div>}
    </section>
  );
};
